import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && user) navigate("/dashboard", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 px-5">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-blue-600 text-white">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <span className="font-display text-xl font-bold tracking-tight text-white">DeviceLox</span>
        </div>
        <div className="rounded-md border border-slate-700 bg-white p-8 shadow-2xl" data-testid="login-card">
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Owner Login</h1>
          <p className="mt-1 text-sm text-slate-500">Sign in to review registrations.</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="owner@devicelox.com" />
            </div>
            <div>
              <Label htmlFor="password" className="mb-1.5 block text-sm font-medium text-slate-700">Password</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full rounded-md bg-blue-600 text-white hover:bg-blue-500">
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </div>
        <p className="mt-6 text-center text-sm text-slate-400">
          <a href="/" className="hover:text-white">← Back to site</a>
        </p>
      </div>
    </div>
  );
}
